import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { FloatingInput } from "@/components/ui/floating-input";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface Filters {
  primaryCategories: string[];
  secondaryCategories: string[];
  materialidades: string[];
  csatMin: number;
  csatMax: number;
  costMin: number;
  costMax: number;
  contractType: ("determinado" | "indeterminado")[];
  contractStatus: ("ativo" | "expirado")[];
  simulationAptness: ("apto" | "inapto")[];
  contractPending: boolean;
  costPending: boolean;
  editorialPending: boolean;
  expiration_from?: string;
  expiration_to?: string;
}

interface FilterModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}

export function FilterModal({ open, onOpenChange, filters, onFiltersChange }: FilterModalProps) {
  const [activeTab, setActiveTab] = useState("identificacao");
  const [csatRange, setCsatRange] = useState([filters.csatMin || 0, filters.csatMax || 5]);
  const [custoRange, setCustoRange] = useState([filters.costMin || 1, filters.costMax || 50]);
  const [tipoContrato, setTipoContrato] = useState<("determinado" | "indeterminado")[]>(filters.contractType || []);
  const [statusContrato, setStatusContrato] = useState<("ativo" | "expirado")[]>(filters.contractStatus || []);
  const [simulationAptness, setSimulationAptness] = useState<("apto" | "inapto")[]>(filters.simulationAptness || []);
  const [selectedPrimaryCategories, setSelectedPrimaryCategories] = useState<string[]>(filters.primaryCategories || []);
  const [selectedSecondaryCategories, setSelectedSecondaryCategories] = useState<string[]>(filters.secondaryCategories || []);
  const [selectedMaterialidades, setSelectedMaterialidades] = useState<string[]>(filters.materialidades || []);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");

  // Sync filter states when modal opens or filters change
  useEffect(() => {
    if (open) {
      setCsatRange([filters.csatMin || 0, filters.csatMax || 5]);
      setCustoRange([filters.costMin || 1, filters.costMax || 50]);
      setTipoContrato(filters.contractType || []);
      setStatusContrato(filters.contractStatus || []);
      setSimulationAptness(filters.simulationAptness || []);
      setSelectedPrimaryCategories(filters.primaryCategories || []);
      setSelectedSecondaryCategories(filters.secondaryCategories || []);
      setSelectedMaterialidades(filters.materialidades || []);
      // sync expiration date inputs from filters (ISO -> DD/MM/YYYY)
      setDateStart(isoToBr(filters.expiration_from));
      setDateEnd(isoToBr(filters.expiration_to));
    }
  }, [open, filters]);

  const toggleTipoContrato = (tipo: "determinado" | "indeterminado") => {
    setTipoContrato((prev) =>
      prev.includes(tipo)
        ? prev.filter((t) => t !== tipo)
        : [...prev, tipo]
    );
  };

  const primaryCategories = ["A1", "A2", "B1", "B2", "C", "D", "E"];
  const secondaryCategories = ["A1", "A2", "B1", "B2", "C", "D", "E"];
  const materialidades = ["Capa Dura", "Brochura", "Pano", "Banho", "Cartonado"];

  const togglePrimaryCategory = (category: string) => {
    setSelectedPrimaryCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const toggleSecondaryCategory = (category: string) => {
    setSelectedSecondaryCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    );
  };

  const toggleMaterialidade = (material: string) => {
    setSelectedMaterialidades((prev) =>
      prev.includes(material)
        ? prev.filter((m) => m !== material)
        : [...prev, material]
    );
  };

  const toggleStatusContrato = (status: "ativo" | "expirado") => {
    setStatusContrato((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const toggleSimulationAptness = (aptness: "apto" | "inapto") => {
    // Make selection mutually exclusive: selecting one will deselect the other.
    setSimulationAptness((prev) => (prev.includes(aptness) ? [] : [aptness]));
  };

  const formatDateBR = (value: string): string => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 4) return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
  };

  const brToIso = (br: string): string | "" => {
    const numbers = br.replace(/\D/g, "");
    if (numbers.length !== 8) return "";
    const day = numbers.slice(0, 2);
    const month = numbers.slice(2, 4);
    const year = numbers.slice(4, 8);
    return `${year}-${month}-${day}`;
  };

  const isoToBr = (iso: string | undefined): string => {
    if (!iso) return "";
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return "";
    const [, year, month, day] = m;
    return `${day}/${month}/${year}`;
  };

  const handleApplyFilters = () => {
    onFiltersChange({
      primaryCategories: selectedPrimaryCategories,
      secondaryCategories: selectedSecondaryCategories,
      materialidades: selectedMaterialidades,
      csatMin: csatRange[0] || 0,
      csatMax: csatRange[1] || 5,
      costMin: custoRange[0] || 1,
      costMax: custoRange[1] || 50,
      contractType: tipoContrato,
      contractStatus: statusContrato,
      simulationAptness: simulationAptness,
      contractPending: filters.contractPending,
      costPending: filters.costPending,
      editorialPending: filters.editorialPending,
      expiration_from: brToIso(dateStart),
      expiration_to: brToIso(dateEnd),
    });
    onOpenChange(false);
  };

  const handleRemoveFilters = () => {
    setSelectedPrimaryCategories([]);
    setSelectedSecondaryCategories([]);
    setSelectedMaterialidades([]);
    setCsatRange([0, 5]);
    setCustoRange([1, 50]);
    setTipoContrato([]);
    setStatusContrato([]);
    setSimulationAptness([]);
    setDateStart("");
    setDateEnd("");
    onFiltersChange({
      primaryCategories: [],
      secondaryCategories: [],
      materialidades: [],
      csatMin: 0,
      csatMax: 5,
      costMin: 1,
      costMax: 50,
      contractType: [],
      contractStatus: [],
      simulationAptness: [],
      contractPending: false,
      costPending: false,
      editorialPending: false,
      expiration_from: "",
      expiration_to: "",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] min-h-[650px] p-0 gap-0 rounded-[28px] overflow-hidden bg-[#F3FAFB] shadow-[0_1px_2px_0_rgba(0,0,0,0.3),0_1px_3px_1px_rgba(0,0,0,0.15)] border-0 [&>button]:hidden">
        <DialogTitle className="sr-only">Filtrar por categorias</DialogTitle>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full flex flex-col h-full">
          <div className="bg-[#EDF4F5] pt-6">
            <div className="px-6 pb-4">
              <h2 className="text-2xl font-normal text-[#151C1D]">
                Filtrar
              </h2>
            </div>

            <TabsList className="w-full h-12 bg-[#EDF4F5] rounded-none p-0 gap-0">
              <TabsTrigger
                value="identificacao"
                className="flex-1 h-full rounded-none border-b-2 data-[state=active]:border-[#00646B] data-[state=inactive]:border-transparent bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none relative"
              >
                <div className="flex items-center gap-1">
                  <span
                    className={cn(
                      "text-sm font-normal",
                      activeTab === "identificacao"
                        ? "text-[#151C1D]"
                        : "text-[#3B494A]",
                    )}
                  >
                    Identificação
                  </span>
                  <div className="flex items-center justify-center w-2 h-2 rounded-full bg-[#BA1A1A]" />
                </div>
              </TabsTrigger>

              <TabsTrigger
                value="custo"
                className="flex-1 h-full rounded-none border-b-2 data-[state=active]:border-[#00646B] data-[state=inactive]:border-transparent bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                <div className="flex items-center gap-1">
                  <span
                    className={cn(
                      "text-sm font-normal",
                      activeTab === "custo"
                        ? "text-[#151C1D]"
                        : "text-[#3B494A]",
                    )}
                  >
                    Custo & Contrato
                  </span>
                  <div className="flex items-center justify-center w-2 h-2 rounded-full bg-[#BA1A1A]" />
                </div>
              </TabsTrigger>

              <TabsTrigger
                value="simulacao"
                className="flex-1 h-full rounded-none border-b-2 data-[state=active]:border-[#00646B] data-[state=inactive]:border-transparent bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none"
              >
                <span
                  className={cn(
                    "text-sm font-normal",
                    activeTab === "simulacao"
                      ? "text-[#151C1D]"
                      : "text-[#3B494A]",
                  )}
                >
                  Simulação
                </span>
              </TabsTrigger>
            </TabsList>

            <div className="h-px bg-[#BAC9CA]" />
          </div>

          <div className="flex-1 overflow-y-auto px-6">
            <TabsContent value="identificacao" className="mt-0 space-y-3">
              <div className="space-y-1">
                <div className="pt-3">
                  <label className="text-xs font-medium text-[#3B494A]">
                    Categoria Primária
                  </label>
                </div>
                <div className="flex flex-wrap gap-x-2 gap-y-4 p-2">
                  {primaryCategories.map((category) => {
                    const isSelected = selectedPrimaryCategories.includes(category);
                    return (
                      <button
                        key={category}
                        onClick={() => togglePrimaryCategory(category)}
                        className={cn(
                          "h-8 px-4 rounded-lg border text-sm font-normal transition-colors",
                          isSelected
                            ? "border-[#00646B] bg-[#E0F2F1] text-[#00646B]"
                            : "border-[#BAC9CA] bg-white text-[#3B494A] hover:bg-[#EDF4F5]"
                        )}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1">
                <div className="pt-3">
                  <label className="text-xs font-medium text-[#3B494A]">
                    Categoria Secundária
                  </label>
                </div>
                <div className="flex flex-wrap gap-x-2 gap-y-4 p-2">
                  {secondaryCategories.map((category) => {
                    const isSelected = selectedSecondaryCategories.includes(category);
                    return (
                      <button
                        key={category}
                        onClick={() => toggleSecondaryCategory(category)}
                        className={cn(
                          "h-8 px-4 rounded-lg border text-sm font-normal transition-colors",
                          isSelected
                            ? "border-[#00646B] bg-[#E0F2F1] text-[#00646B]"
                            : "border-[#BAC9CA] bg-white text-[#3B494A] hover:bg-[#EDF4F5]"
                        )}
                      >
                        {category}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1">
                <div className="pt-3">
                  <label className="text-xs font-medium text-[#3B494A]">
                    Materialidades
                  </label>
                </div>
                <div className="flex flex-wrap gap-x-2 gap-y-4 p-2">
                  {materialidades.map((material) => {
                    const isSelected = selectedMaterialidades.includes(material);
                    return (
                      <button
                        key={material}
                        onClick={() => toggleMaterialidade(material)}
                        className={cn(
                          "h-8 px-4 rounded-lg border text-sm font-normal transition-colors flex items-center gap-2",
                          isSelected
                            ? "border-[#00646B] bg-[#E0F2F1] text-[#00646B]"
                            : "border-[#BAC9CA] bg-white text-[#3B494A] hover:bg-[#EDF4F5]"
                        )}
                      >
                        {material}
                        {material === "Cartonado" && (
                          <ChevronDown className="w-[18px] h-[18px]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-1">
                <div className="pt-3">
                  <label className="text-xs font-medium text-[#3B494A]">
                    Nota CSAT
                  </label>
                </div>
                <div className="flex items-center gap-3 py-2 px-1">
                  <span className="text-sm font-medium text-[#3B494A]">{csatRange[0]?.toFixed(1) || 0}</span>
                  <div className="flex-1 relative py-2">
                    <Slider
                      value={csatRange}
                      onValueChange={setCsatRange}
                      max={5}
                      step={0.1}
                      className="csat-slider w-full"
                    />
                  </div>
                  <span className="text-sm font-medium text-[#3B494A]">{csatRange[1]?.toFixed(1) || 5}</span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="custo" className="mt-0 space-y-3">
              <div className="space-y-1">
                <div className="pt-3">
                  <label className="text-xs font-medium text-[#3B494A]">
                    Custo
                  </label>
                </div>
                <div className="flex items-center gap-3 py-2 px-1">
                  <span className="text-sm font-normal text-[#3B494A]">R$ {custoRange[0]?.toFixed(0) || 1}</span>
                  <div className="flex-1 relative py-2">
                    <Slider
                      value={custoRange}
                      onValueChange={setCustoRange}
                      max={50}
                      step={0.5}
                      className="custo-slider w-full"
                    />
                  </div>
                  <span className="text-sm font-normal text-[#3B494A]">R$ {custoRange[1]?.toFixed(0) || 50}</span>
                </div>
              </div>

              <div className="h-px bg-[#BAC9CA] my-3" />

              <div className="space-y-2">
                <div className="pt-3">
                  <label className="text-xs font-medium text-[#3B494A]">
                    Tipo de Contrato
                  </label>
                </div>
                <div className="flex items-center gap-5">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tipoContrato.includes("determinado")}
                      onChange={() => toggleTipoContrato("determinado")}
                      className="w-[18px] h-[18px] cursor-pointer accent-[#00646B] rounded-sm"
                    />
                    <span className="text-base font-normal text-[#151C1D]">Determinado</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tipoContrato.includes("indeterminado")}
                      onChange={() => toggleTipoContrato("indeterminado")}
                      className="w-[18px] h-[18px] cursor-pointer accent-[#00646B] rounded-sm"
                    />
                    <span className="text-base font-normal text-[#151C1D]">Indeterminado</span>
                  </label>
                </div>
              </div>

              <div className="space-y-1">
                <div className="pt-3">
                  <label className="text-xs font-medium text-[#3B494A]">
                    Data de Expiração
                  </label>
                </div>
                <div className="flex items-end gap-3 pt-3 pb-2">
                  <span className="text-sm font-normal text-[#3B494A] mb-0">Entre</span>
                  <div className="flex-1">
                    <FloatingInput
                      type="text"
                      label="Data de início"
                      value={dateStart}
                      onChange={(e) => setDateStart(formatDateBR(e.target.value))}
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                    />
                  </div>
                  <span className="text-sm font-normal text-[#3B494A] mb-0">e</span>
                  <div className="flex-1">
                    <FloatingInput
                      type="text"
                      label="Data final"
                      value={dateEnd}
                      onChange={(e) => setDateEnd(formatDateBR(e.target.value))}
                      placeholder="DD/MM/AAAA"
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="pt-3">
                  <label className="text-xs font-medium text-[#3B494A]">
                    Status do Contrato
                  </label>
                </div>
                <div className="flex items-center gap-5">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={statusContrato.includes("ativo")}
                      onChange={() => toggleStatusContrato("ativo")}
                      className="w-[18px] h-[18px] cursor-pointer accent-[#00646B] rounded-sm"
                    />
                    <span className="text-base font-normal text-[#151C1D]">Ativo</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={statusContrato.includes("expirado")}
                      onChange={() => toggleStatusContrato("expirado")}
                      className="w-[18px] h-[18px] cursor-pointer accent-[#00646B] rounded-sm"
                    />
                    <span className="text-base font-normal text-[#151C1D]">Expirado</span>
                  </label>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="simulacao" className="mt-0 space-y-3">
              <div className="space-y-2">
                <div className="pt-3">
                  <label className="text-xs font-medium text-[#3B494A]">
                    Apto para Simulação?
                  </label>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="flex items-center gap-3 cursor-pointer p-2 hover:bg-[#F3FAFB] rounded">
                    <input
                      type="radio"
                      name="simulationAptness"
                      value="apto"
                      checked={simulationAptness.includes("apto")}
                      onChange={() => {
                        setSimulationAptness(["apto"]);
                      }}
                      className="w-4 h-4 cursor-pointer accent-[#00646B]"
                    />
                    <span className="text-sm font-normal text-[#3B494A]">Apto</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer p-2 hover:bg-[#F3FAFB] rounded">
                    <input
                      type="radio"
                      name="simulationAptness"
                      value="inapto"
                      checked={simulationAptness.includes("inapto")}
                      onChange={() => setSimulationAptness(["inapto"])}
                      className="w-4 h-4 cursor-pointer accent-[#00646B]"
                    />
                    <span className="text-sm font-normal text-[#3B494A]">Inapto</span>
                  </label>
                </div>
              </div>
            </TabsContent>
          </div>

          <div className="flex items-center justify-between gap-2 px-4 pt-2 pb-4 bg-[#EDF4F5]">
            <button
              onClick={handleRemoveFilters}
              className="text-[#00646B] text-sm font-normal hover:underline cursor-pointer bg-transparent border-none p-0 hover:bg-transparent"
            >
              Remover Filtros
            </button>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="h-12 px-4 text-[#00646B] text-sm font-normal hover:bg-transparent"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleApplyFilters}
                className="h-12 px-4 bg-[#00646B] hover:bg-[#00646B]/90 text-white text-sm font-normal rounded-xl"
              >
                Aplicar Filtros
              </Button>
            </div>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
